import React from 'react';
import { Check, Lock, Loader2 } from 'lucide-react';
import type { ApprovalStep } from '../utils/executeChrome';

export interface ApprovalChainProps {
  steps: ApprovalStep[];
  compact?: boolean;
  /** Complete the current (manual) stage — sequential unlock */
  onCompleteCurrent?: () => void;
  /** True while the complete API is in flight */
  completing?: boolean;
}

export const ApprovalChain: React.FC<ApprovalChainProps> = ({
  steps,
  compact = false,
  onCompleteCurrent,
  completing = false,
}) => {
  if (compact) {
    return (
      <ol className="flex items-center gap-1" aria-label="Approval progress">
        {steps.map((step, i) => (
          <li key={step.id ?? step.label} className="flex items-center gap-1" title={`${step.label} — ${step.state}`}>
            <span
              className={`w-2.5 h-2.5 rounded-full ${
                step.state === 'done'
                  ? 'bg-emerald-500'
                  : step.state === 'current'
                    ? 'bg-[#FF6E4C] ring-2 ring-[#FF6E4C]/30'
                    : 'bg-slate-200'
              }`}
            />
            {i < steps.length - 1 && (
              <span className={`w-4 h-0.5 ${step.state === 'done' ? 'bg-emerald-300' : 'bg-slate-200'}`} />
            )}
          </li>
        ))}
      </ol>
    );
  }

  return (
    <ol aria-label="Approval & sign-off stages">
      {steps.map((step, i) => {
        const isLocked = step.state === 'pending';
        const isCurrent = step.state === 'current';
        const canAdvance = isCurrent && Boolean(step.actionLabel) && Boolean(onCompleteCurrent);

        return (
          <li key={step.id ?? step.label} className="flex gap-3">
            <div className="flex flex-col items-center">
              <span
                className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${
                  step.state === 'done'
                    ? 'bg-emerald-500 text-white'
                    : isCurrent
                      ? 'bg-[#FF6E4C] text-white'
                      : 'bg-slate-100 text-slate-400 border border-slate-200'
                }`}
              >
                {step.state === 'done' ? (
                  <Check className="w-3.5 h-3.5" />
                ) : isLocked ? (
                  <Lock className="w-3 h-3" />
                ) : (
                  <span className="text-[10px] font-bold">{i + 1}</span>
                )}
              </span>
              {i < steps.length - 1 && (
                <span
                  className={`w-0.5 flex-1 min-h-[14px] ${
                    step.state === 'done' ? 'bg-emerald-300' : 'bg-slate-200'
                  }`}
                />
              )}
            </div>
            <div className="pb-4 min-w-0">
              <p
                className={`text-xs font-bold leading-snug ${
                  isLocked ? 'text-[#717182]' : 'text-[#18365E]'
                }`}
              >
                {step.label}
                {step.cfo && (
                  <span className="ml-1.5 text-[9px] font-bold uppercase tracking-wide text-[#FF6E4C] bg-[#FFF1ED] px-1.5 py-0.5 rounded">
                    CFO gate
                  </span>
                )}
              </p>
              {step.note && (
                <p className="text-[11px] text-[#717182] mt-0.5 leading-relaxed">{step.note}</p>
              )}
              {step.state === 'done' && (
                <span className="inline-flex items-center gap-1 mt-1.5 text-[10px] font-bold text-emerald-700 uppercase tracking-wide">
                  <Check className="w-3 h-3" />
                  Completed
                </span>
              )}
              {canAdvance && (
                <button
                  type="button"
                  onClick={onCompleteCurrent}
                  disabled={completing}
                  aria-busy={completing}
                  className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-semibold text-white bg-[#18365E] hover:bg-[#122a4a] px-2.5 py-1.5 rounded-md transition-colors disabled:opacity-60 disabled:pointer-events-none"
                >
                  {completing && <Loader2 className="w-3 h-3 animate-spin" aria-hidden="true" />}
                  {completing ? 'Completing…' : (step.actionLabel ?? 'Complete')}
                </button>
              )}
              {isLocked && (
                <p className="mt-1 text-[10px] text-[#94a3b8]">Complete previous stage to unlock</p>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
};

export default ApprovalChain;
